import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
    FaPlayCircle, FaFilter, FaDownload, 
    FaSyncAlt, FaPhoneAlt, FaPhoneSlash, FaSearch, FaListUl
} from 'react-icons/fa';

interface CallLog {
    id: number;
    agent_extension: string;
    customer_number: string;
    status: string;
    duration: number;
    recording_url: string;
    created_at: string;
}

const CallHistory: React.FC = () => {
    const [logs, setLogs] = useState<CallLog[]>([]);
    const [loading, setLoading] = useState(true);
    
    // --- Checkbox Selection State ---
    const [selectedLogs, setSelectedLogs] = useState<number[]>([]);

    // --- Filter States ---
    const [showFilter, setShowFilter] = useState(false);
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterAgent, setFilterAgent] = useState('All');
    const [filterSearch, setFilterSearch] = useState('');
    
    // Date Filters (Default to Today)
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    
    const filterRef = useRef<HTMLDivElement>(null);

    // --- ROLE & EXTENSION LOGIC ---
    let username = "Unknown";
    let isAgent = false;
    try {
        const token = localStorage.getItem('token');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            username = payload.sub || payload.username || "Unknown";
            isAgent = JSON.stringify(payload).toUpperCase().includes('AGENT');
        }
    } catch(e) {}

    // Identify current agent's extension
    let currentAgentExt = "";
    if (isAgent) {
        if (username === 'deep123') currentAgentExt = "8037";
        // Agar koi aur agent ho toh uski extension yahan set karein
    }

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                role: isAgent ? 'AGENT' : 'ADMIN',
                agentExt: isAgent ? currentAgentExt : (filterAgent === 'All' ? '' : filterAgent),
                status: filterStatus === 'All' ? '' : filterStatus,
                search: filterSearch,
                startDate: startDate,
                endDate: endDate
            }).toString();

            // const response = await fetch(`http://localhost:8483/api/call/history?${queryParams}`);
            const response = await fetch(`https://www.ayushvatika.com/api/call/history?${queryParams}`);
            const data = await response.json();
            if (data.success) {
                setLogs(data.data);
                setSelectedLogs([]); // Clear selection on new fetch
            }
        } catch (error) {
            console.error("Error fetching call logs", error);
        } finally {
            setLoading(false);
            setShowFilter(false); 
        }
    };

    // Load initial data whenever dates change
    useEffect(() => { fetchLogs(); }, [startDate, endDate]);

    // Handle clicking outside filter popup
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setShowFilter(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- HELPERS ---
    const maskMobile = (mobile: string) => {
        if (!isAgent) return mobile; 
        if (!mobile || mobile.length < 10) return mobile || 'N/A';
        return mobile.substring(0, 2) + "******" + mobile.substring(8); 
    };

    const formatDuration = (seconds: number) => {
        if (!seconds) return "00:00:00";
        const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const getAgentNameFromExt = (ext: string) => {
        if (!ext) return "System";
        if (ext.includes('8037')) return "deep123";
        if (ext.includes('8158')) return "Admin";
        return "Agent";
    };

    const clearFilters = () => {
        setFilterStatus('All');
        setFilterAgent('All');
        setFilterSearch('');
    };

    // --- SELECTION LOGIC ---
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedLogs(logs.map(log => log.id));
        } else {
            setSelectedLogs([]);
        }
    };

    const handleToggleSelect = (id: number) => {
        if (selectedLogs.includes(id)) {
            setSelectedLogs(selectedLogs.filter(logId => logId !== id));
        } else {
            setSelectedLogs([...selectedLogs, id]);
        }
    };

    return (
        <AdminLayout>
            <div className="p-4 md:p-6 bg-[#f3f4f6] dark:bg-gray-900 min-h-screen font-sans">
                
                {/* --- HEADER --- */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6 p-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        
                        <div className="flex items-center gap-4 flex-wrap">
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white border-l-4 border-orange-500 pl-3 mr-2">
                                Call Logs
                            </h2>
                            
                            {/* Date Filters */}
                            <div className="flex items-center gap-2 border border-orange-300 rounded px-2 py-1 bg-orange-50 dark:bg-orange-900/20 text-sm">
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent text-orange-700 outline-none cursor-pointer" />
                                <span className="text-orange-400">-</span>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent text-orange-700 outline-none cursor-pointer" />
                            </div>

                            {/* Total Call Count Tag */}
                            <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold border border-blue-200">
                                <FaListUl /> Total Calls: {logs.length}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 relative w-full md:w-auto">
                            
                            {/* Number Search Box */}
                            <div className="relative hidden md:block">
                                <input 
                                    type="text" 
                                    placeholder="Search Direct Number..." 
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                                    className="border border-orange-300 text-orange-700 px-3 py-1.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder-orange-300"
                                />
                                <button onClick={fetchLogs} className="absolute right-2 top-1.5 text-orange-500 hover:text-orange-700 cursor-pointer">
                                    <FaSearch size={14} />
                                </button>
                            </div>

                            <button onClick={() => setShowFilter(!showFilter)} className="flex items-center gap-2 border border-orange-400 text-orange-600 hover:bg-orange-50 px-4 py-1.5 rounded text-sm font-bold shadow-sm">
                                <FaFilter /> FILTER {(filterStatus !== 'All' || filterAgent !== 'All') ? '*' : ''}
                            </button>

                            <button onClick={fetchLogs} className="border border-gray-300 text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded shadow-sm">
                                <FaSyncAlt />
                            </button>

                            {/* FILTER POPUP */}
                            {showFilter && (
                                <div ref={filterRef} className="absolute top-10 right-0 w-80 bg-white shadow-2xl rounded-lg border border-gray-200 z-50 p-4">
                                    <div className="flex gap-2 mb-4">
                                        <button onClick={fetchLogs} className="flex-1 bg-[#e45a23] text-white py-1.5 rounded font-bold text-sm hover:bg-[#cf4e1b]">APPLY</button>
                                        <button onClick={clearFilters} className="flex-1 bg-white border border-[#e45a23] text-[#e45a23] py-1.5 rounded font-bold text-sm hover:bg-orange-50">CLEAR</button>
                                    </div>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <label className="block text-gray-500 text-xs mb-1">Call Status</label>
                                            <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} className="w-full border rounded p-2 focus:outline-orange-400">
                                                <option value="All">All Status</option>
                                                <option value="Answered">Answered</option>
                                                <option value="Missed">Missed</option>
                                                <option value="Initiated">Initiated</option>
                                            </select>
                                        </div>
                                        
                                        {!isAgent && (
                                            <div>
                                                <label className="block text-gray-500 text-xs mb-1">Agents Ext / ID</label>
                                                <select value={filterAgent} onChange={(e)=>setFilterAgent(e.target.value)} className="w-full border rounded p-2 focus:outline-orange-400">
                                                    <option value="All">All Agents</option>
                                                    <option value="8158">Admin Default (8158)</option>
                                                    <option value="8037">Deep123 (8037)</option>
                                                </select>
                                            </div>
                                        )}
                                        {/* Mobile View Number Search */}
                                        <div className="block md:hidden">
                                            <label className="block text-gray-500 text-xs mb-1">Customer Number</label>
                                            <input type="text" placeholder="Search number..." value={filterSearch} onChange={(e)=>setFilterSearch(e.target.value)} className="w-full border rounded p-2 focus:outline-orange-400" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- TABLE SECTION --- */}
                <div className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-[#fdf8f4] dark:bg-gray-700 border-b border-gray-200 text-gray-600 font-bold text-[11px] uppercase tracking-wider">
                                <tr>
                                    <th className="p-4 w-10 text-center">
                                        <input 
                                            type="checkbox" 
                                            onChange={handleSelectAll}
                                            checked={logs.length > 0 && selectedLogs.length === logs.length}
                                            className="rounded text-orange-500 focus:ring-orange-500 cursor-pointer w-4 h-4" 
                                        />
                                    </th>
                                    <th className="p-4">Call ID</th>
                                    <th className="p-4">Solution & Time</th>
                                    <th className="p-4">Customer No.</th>
                                    {!isAgent && <th className="p-4">Agent Ext / Name</th>}
                                    <th className="p-4">Duration</th>
                                    <th className="p-4 text-center">Recording</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-gray-500"><FaSyncAlt className="animate-spin inline mr-2"/> Loading...</td></tr>
                                ) : logs.length === 0 ? (
                                    <tr><td colSpan={7} className="p-8 text-center text-gray-500">No call logs found for selected filters.</td></tr>
                                ) : (
                                    logs.map((log, index) => (
                                        <tr key={log.id} className={`${selectedLogs.includes(log.id) ? 'bg-orange-50' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className="p-4 text-center">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedLogs.includes(log.id)}
                                                    onChange={() => handleToggleSelect(log.id)}
                                                    className="rounded text-orange-500 focus:ring-orange-500 cursor-pointer w-4 h-4" 
                                                />
                                            </td>
                                            
                                            <td className="p-4 text-blue-600 font-medium">
                                                {/* Show ID + dummy starting digits to look like Tata */}
                                                {log.id}1778...
                                            </td>

                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    {log.status === 'Answered' ? (
                                                        <div className="text-green-500"><FaPhoneAlt size={14}/></div>
                                                    ) : (
                                                        <div className="text-red-500"><FaPhoneSlash size={16}/></div>
                                                    )}
                                                    <div className="flex flex-col">
                                                        <span className="text-gray-800 font-medium">Clicktocall <span className={`ml-2 text-[10px] px-1.5 rounded-full ${log.status === 'Answered' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{log.status}</span></span>
                                                        <span className="text-gray-500 text-[11px]">
                                                            {new Date(log.created_at).toLocaleString('en-IN', { month: 'short', day: '2-digit', year: 'numeric' })} | {new Date(log.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="p-4 font-mono font-medium text-gray-700">
                                                +{maskMobile(log.customer_number)}
                                            </td>

                                            {!isAgent && (
                                                <td className="p-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-mono text-gray-700">+{log.agent_extension}</span>
                                                        <span className="text-[10px] uppercase font-bold text-blue-600 bg-blue-50 w-max px-2 py-0.5 rounded border border-blue-100 mt-1">
                                                            {getAgentNameFromExt(log.agent_extension)}
                                                        </span>
                                                    </div>
                                                </td>
                                            )}

                                            <td className="p-4 font-mono text-gray-600">
                                                {formatDuration(log.duration)}
                                            </td>

                                            <td className="p-4 text-center">
                                                <div className="flex justify-center">
                                                    {log.recording_url ? (
                                                        <button onClick={() => window.open(log.recording_url)} className="text-[#e45a23] hover:text-[#cf4e1b]">
                                                            <FaPlayCircle size={22} />
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-300">--</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </AdminLayout>
    );
};

export default CallHistory;